import { CustomTableSkeleton } from '@/components_v2/Table/TableSkeleton/CustomTableSkeleton';
import { Button } from '@chakra-ui/react';
import Skeleton from 'react-loading-skeleton';
import {
  ColumnFiltersState,
  ColumnOrderState,
  ColumnPinningState,
  ExpandedState,
  FilterFn,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  Row as RowReactTable,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { valueFocusAriaMessage } from 'react-select/src/accessibility';
import { FixedSizeList } from 'react-window';

import { TableProps } from '../../interfaces';
import { convertColumns, convertItems } from '../../utils/dataConverters';
import SubComponent from '../SubComponent';
import { useTabsViewsContext } from '../TabsViews';
import Column from './components/Column';
import Footer from './components/Footer';
import SingleFooter from './components/SingleFooter';
import Row from './components/Row';
import {
  ColumnsControlsContainer,
  ContainerTable,
  ControlButton,
  Tbody,
  TFooter,
  Thead,
} from './styles';

const filterFnsCustom: Record<string, FilterFn<any>> = {
  customFilter: (row, columnId, filterValue) => {
    return filterValue.includes(row.getValue(columnId));
  },
};

export const InnerTable: React.FC<TableProps> = ({
  columns,
  footer,
  items,
  expanderBackgroundColor,
  headerTextColor,
  headerTextWeight,
  headerBackgroundColor,
  headerBorderColor,
  headerFontSize,
  headerIsAbsolute,
  footerTextColor,
  footerTextWeight,
  footerBackgroundColor,
  footerBorderColor,
  footerFontSize,
  footerIconType,
  footerIconColor,
  footerIconSize,
  rowHeight,
  cellBorderColor,
  cellPadding,
  heatColorStart,
  heatColorEnd,
  cancelToken,
  expandableRows,
  dashMenuOptions,
  onExport,
  parentExpanded,
  level = '',
  setChildHeight,
  subgroupColor,
  columnsFilters,
  singleFooter,
  contentInfo,
  headerLines,
  isVirtualized,
  height,
  scrollbarWidth,
  deltaReversed,
  fetchMoreOnBottomReached,
  isFetching,
  enableColumnsControls,
  pinColumns,
  tableView,
}) => {
  const headerRef = useRef<HTMLTableSectionElement>(null);
  const listRef = useRef<HTMLElement | null>(null);
  const bodyRef = useRef<HTMLTableSectionElement | null>(null);

  const initialVisibilityState = columns.reduce((state, column) => {
    if (!column?.key) return state;

    return {
      ...state,
      [column?.key]: column?.visible,
    };
  }, {});

  const {
    hasTabs,
    fetchNextSubPage,
    subPageFetching,
    subPageFetchingId,
  } = useTabsViewsContext();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [expanded, setExpanded] = React.useState<ExpandedState>({});
  const [columnVisibility, setColumnVisibility] = React.useState<
    VisibilityState
  >(enableColumnsControls ? initialVisibilityState : {});
  const [columnPinning, setColumnPinning] = React.useState<ColumnPinningState>(
    pinColumns || {
      left: [],
      right: [],
    },
  );

  const subLevelNumber = level?.length || 0;

  const { columns: newColumns, restColumns } = useMemo(
    () => convertColumns(columns.filter((column) => column.header !== null)),
    [columns],
  );

  const { values: newItems, restValues: restItems } = useMemo(
    () => convertItems(items, newColumns),
    [items, newColumns],
  );

  const tableIsVirtualized = useMemo(
    () => isVirtualized || !columns.some((column) => !!column.expander),
    [isVirtualized, columns],
  );

  const table = useReactTable({
    data: newItems,
    columns: newColumns,
    state: {
      sorting,
      columnFilters,
      expanded,
      columnVisibility,
      columnPinning,
    },
    onExpandedChange: setExpanded,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    onColumnPinningChange: setColumnPinning,
    filterFns: filterFnsCustom,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  const tableColumns = table.getAllColumns();
  const { rows } = table?.getRowModel();

  useEffect(() => {
    const headerElement = headerRef.current;
    if (!headerElement) return undefined;

    const containerElement = headerElement.closest(
      '.responsive-table-container',
    ) as HTMLElement | null;

    const scrollers: HTMLElement[] = [];
    if (containerElement) scrollers.push(containerElement);
    if (bodyRef.current) scrollers.push(bodyRef.current);
    if (listRef.current) scrollers.push(listRef.current);

    if (!scrollers.length) return undefined;

    const handleScroll = (event: Event) => {
      const target = event.target as HTMLElement;
      headerElement.style.transform = `translateX(-${target.scrollLeft}px)`;
    };

    scrollers.forEach((scroller) => {
      scroller.addEventListener('scroll', handleScroll, { passive: true });
    });

    return () => {
      headerElement.style.transform = '';
      scrollers.forEach((scroller) => {
        scroller.removeEventListener('scroll', handleScroll);
      });
    };
  }, [rows.length, tableIsVirtualized]);

  const renderRowSubComponent = useCallback(
    (
      id: number,
      rowId: string,
      isExpanded: boolean | undefined,
      index: number,
    ) => {
      const { type, data, clearSizeCache } =
        restItems[id]?.subComponent || restItems[id]?.subComponentDynamic;

      return (
        <SubComponent
          type={type}
          data={data}
          cancelToken={cancelToken}
          parentExpanded={isExpanded}
          level={level.concat(rowId)}
          expandHeight={setChildHeight}
          clearCache={() => clearSizeCache(index, true)}
          tableView={tableView}
        />
      );
    },

    [cancelToken, restItems, level, setChildHeight, tableView],
  );

  const renderTableColumnControls = useCallback(() => {
    const availableColumnsToHide = tableColumns.filter(
      (column) => column.columnDef.enableHiding,
    );

    if (isEmpty(availableColumnsToHide)) return null;

    return (
      <ColumnsControlsContainer>
        {availableColumnsToHide.map((column) => {
          return (
            <ControlButton
              type="button"
              onClick={(event) => {
                column.getToggleVisibilityHandler()(event);
                column.pin('left');
              }}
              isActive={column.getIsVisible()}
            >
              {column.columnDef.header}
            </ControlButton>
          );
        })}
      </ColumnsControlsContainer>
    );
  }, [tableColumns]);

  const renderTableHeader = useCallback(
    () => (
      <Thead
        ref={headerRef}
        className="HeaderContainer"
        headerIsAbsolute={headerIsAbsolute ?? false}
        ref={headerRef}
        style={{ paddingRight: tableIsVirtualized ? '16px' : '0px' }}
      >
        {table.getHeaderGroups().map((headerGroup) => {
          return (
            <Column
              key={headerGroup.id}
              restColumns={restColumns}
              headerGroup={headerGroup}
              headerBackgroundColor={headerBackgroundColor}
              headerBorderColor={headerBorderColor}
              headerTextColor={headerTextColor}
              headerTextWeight={headerTextWeight}
              headerFontSize={headerFontSize}
              rowHeight={rowHeight}
              columnsFilters={columnsFilters}
            />
          );
        })}
      </Thead>
    ),
    [
      columnsFilters,
      headerBackgroundColor,
      headerBorderColor,
      headerFontSize,
      headerIsAbsolute,
      headerTextColor,
      headerTextWeight,
      restColumns,
      rowHeight,
      table,
    ],
  );

  const renderTableFooter = useCallback(() => {
    if (footer) {
      return (
        <TFooter className="FooterContainer" rowHeight={rowHeight} style={{paddingRight: tableIsVirtualized ? '16px' : '0px'}}>
          {singleFooter ? (
            <SingleFooter
              footer={footer}
              table={table}
              footerBackgroundColor={footerBackgroundColor}
              footerBorderColor={footerBorderColor}
              footerTextColor={footerTextColor}
              footerTextWeight={footerTextWeight}
              footerFontSize={footerFontSize}
              footerIconType={footerIconType as 'positive' | 'negative' | 'equal' | undefined}
              footerIconColor={footerIconColor}
              footerIconSize={footerIconSize}
              restColumns={restColumns}
            />
          ) : (
            table
              .getHeaderGroups()
              .map((headerGroup, index) => (
                <Footer
                  key={headerGroup.id}
                  footer={index > 0 ? footer.slice(1) : footer}
                  headerGroup={headerGroup}
                  footerBackgroundColor={footerBackgroundColor}
                  footerBorderColor={footerBorderColor}
                  footerTextColor={footerTextColor}
                  footerTextWeight={footerTextWeight}
                  footerFontSize={footerFontSize}
                  footerIconType={footerIconType}
                  footerIconColor={footerIconColor}
                  footerIconSize={footerIconSize}
                  restColumns={restColumns}
                />
              ))
          )}
        </TFooter>
      );
    }
    return null;
  }, [
    footer,
    rowHeight,
    table,
    footerBackgroundColor,
    footerBorderColor,
    footerTextColor,
    footerTextWeight,
    footerFontSize,
    footerIconType,
    footerIconColor,
    footerIconSize,
    singleFooter,
    restColumns,
    columnVisibility,
    columnPinning,
  ]);

  const renderTableRows = useMemo(() => {
    const defaultHeight = !height || Number.isNaN(height) ? 400 : height;

    const renderLoadMoreButton = (
      expandId: string,
      columnLabel?: string,
      value?: string,
    ) => {
      const isLoading = subPageFetching && subPageFetchingId === expandId;
      const label =
        columnLabel && value
          ? `Carregar mais de ${columnLabel} - ${value}`
          : 'Carregar mais...';
      return (
        <tr
          style={{
            display: 'flex',
            width: '100%',
            height: rowHeight,
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: '1px solid #ececec',
          }}
        >
          <td
            style={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Button
              size="xs"
              isLoading={isLoading}
              isDisabled={isLoading}
              onClick={() => fetchNextSubPage(expandId)}
            >
              {label}
            </Button>
          </td>
        </tr>
      );
    };

    return (
      <>
        {tableIsVirtualized ? (
          <FixedSizeList
            height={
              footer
                ? defaultHeight - (headerLines ? 35 * headerLines : 120)
                : defaultHeight - 40
            }
            itemSize={rowHeight}
            width="100%"
            itemCount={rows.length}
            outerRef={listRef}
            className="VirtualizedList"
            style={{
              overflow: 'auto',
              whiteSpace: 'nowrap',
              scrollbarWidth,
              minHeight: '8rem',
            }}
            onScroll={() => {
              if (contentInfo?.isPaginate && listRef.current) {
                fetchMoreOnBottomReached(listRef.current as HTMLDivElement);
              }
            }}
          >
            {({ index, style }) => {
              const row = rows[index];
              const dataIndex = parseInt(row.id, 10);
              const sentinelData = restItems[dataIndex]?.__loadMoreSentinel;

              if (sentinelData?.expandId) {
                return (
                  <div
                    style={{
                      ...style,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {renderLoadMoreButton(
                      sentinelData.expandId,
                      sentinelData.columnLabel,
                      sentinelData.value,
                    )}
                  </div>
                );
              }

              return (
                <div style={style}>
                  <Row
                    cellBorderColor={cellBorderColor}
                    cellPadding={cellPadding}
                    restItem={restItems}
                    newItems={newItems}
                    onExport={onExport}
                    expandableRows={expandableRows}
                    row={row}
                    rowHeight={rowHeight}
                    heatColorEnd={heatColorEnd}
                    heatColorStart={heatColorStart}
                    dashMenuOptions={dashMenuOptions}
                    expanderBackgroundColor={expanderBackgroundColor}
                    parentExpanded={parentExpanded}
                    level={level}
                    subgroupColor={subgroupColor}
                    deltaReversed={deltaReversed}
                    contentInfo={contentInfo}
                  />
                </div>
              );
            }}
          </FixedSizeList>
        ) : (
          rows?.map((row, i) => {
            const dataIndex = parseInt(row.id, 10);
            const sentinelData = restItems[dataIndex]?.__loadMoreSentinel;

            if (sentinelData?.expandId) {
              return (
                <React.Fragment key={`row-${row.id}`}>
                  {renderLoadMoreButton(
                    sentinelData.expandId,
                    sentinelData.columnLabel,
                    sentinelData.value,
                  )}
                </React.Fragment>
              );
            }

            return (
              <React.Fragment key={`row-${row.id}`}>
                <Row
                  cellBorderColor={cellBorderColor}
                  cellPadding={cellPadding}
                  restItem={restItems}
                  expandableRows={expandableRows}
                  rowHeight={rowHeight}
                  row={row}
                  heatColorEnd={heatColorEnd}
                  heatColorStart={heatColorStart}
                  dashMenuOptions={dashMenuOptions}
                  onExport={onExport}
                  expanderBackgroundColor={expanderBackgroundColor}
                  parentExpanded={parentExpanded}
                  level={level}
                  subgroupColor={subgroupColor}
                  contentInfo={contentInfo}
                  closeRows={() => table?.setExpanded({})}
                />
                {row.getIsExpanded() ? (
                  <div>
                    {renderRowSubComponent(i, row.id, row.getIsExpanded(), i)}
                  </div>
                ) : null}
              </React.Fragment>
            );
          })
        )}
      </>
    );
  }, [
    tableIsVirtualized,
    hasTabs,
    rows,
    footer,
    height,
    headerLines,
    rowHeight,
    scrollbarWidth,
    cellBorderColor,
    cellPadding,
    restItems,
    newItems,
    onExport,
    expandableRows,
    heatColorEnd,
    heatColorStart,
    dashMenuOptions,
    expanderBackgroundColor,
    parentExpanded,
    level,
    subgroupColor,
    deltaReversed,
    contentInfo,
    renderRowSubComponent,
    table,
    columnVisibility,
    columnPinning,
    fetchNextSubPage,
    subPageFetching,
    subPageFetchingId,
  ]);

  return (
    <>
      {enableColumnsControls && renderTableColumnControls()}
      <ContainerTable
        footer={!!footer}
        className="responsive-table-container"
        id="responsive-table-container"
        parentExpanded={parentExpanded}
        subLevelNumber={subLevelNumber}
        subgroupColor={subgroupColor}
        enableColumnsControls={enableColumnsControls}
      >
        {renderTableHeader()}
        <Tbody
          ref={bodyRef}
          className="ComumList"
          style={
            tableIsVirtualized
              ? { overflow: 'hidden' }
              : {
                position: 'relative',
                overflow: contentInfo?.isPaginate ? 'scroll' : '',
              }
          }
          onScroll={(e) => {
            if (contentInfo?.isPaginate) {
              fetchMoreOnBottomReached(e.target as HTMLDivElement);
            }
          }}
        >
          {renderTableRows}
          {isFetching && (
            <div style={{ width: '100%', padding: '8px 0' }}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton
                  key={i}
                  style={{ height: 24, width: '100%', marginBottom: 4, borderRadius: 4 }}
                />
              ))}
            </div>
          )}
        </Tbody>

        {renderTableFooter()}
      </ContainerTable>
    </>
  );
};
